import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, EyeOff, Calendar, MapPin, Users, GraduationCap, Inbox, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  title: '',
  theme: '',
  date: '',
  location: '',
  expected_participants: 20,
  conditions: '',
  expected_results: '',
  candidate_category: '',
  modality: 'Présentiel',
  description: '',
  google_form_url: '',
  published: true
};

const MODALITIES = ['Présentiel', 'En ligne', 'Hybride'];

export default function FormationsManager() {
  const [tab, setTab] = useState('list'); // list | applications
  const [formations, setFormations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [decisionModal, setDecisionModal] = useState(null);
  const [decisionMessage, setDecisionMessage] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([
        axios.get(`${API}/admin/formations`, { withCredentials: true }),
        axios.get(`${API}/admin/formation-applications`, { withCredentials: true })
      ]);
      setFormations(f.data);
      setApplications(a.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, expected_participants: parseInt(formData.expected_participants, 10) || 1 };
      if (editingId) {
        await axios.put(`${API}/admin/formations/${editingId}`, payload, { withCredentials: true });
        toast.success('Formation mise à jour');
      } else {
        await axios.post(`${API}/admin/formations`, payload, { withCredentials: true });
        toast.success('Formation publiée');
      }
      setFormData(EMPTY); setEditingId(null); setShowForm(false);
      fetchAll();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleEdit = (f) => {
    setEditingId(f.formation_id);
    setFormData({
      title: f.title || '',
      theme: f.theme || '',
      date: f.date ? f.date.split('T')[0] : '',
      location: f.location || '',
      expected_participants: f.expected_participants || 20,
      conditions: f.conditions || '',
      expected_results: f.expected_results || '',
      candidate_category: f.candidate_category || '',
      modality: f.modality || 'Présentiel',
      description: f.description || '',
      google_form_url: f.google_form_url || '',
      published: f.published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette formation et toutes ses candidatures ?')) return;
    try {
      await axios.delete(`${API}/admin/formations/${id}`, { withCredentials: true });
      toast.success('Formation supprimée');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const togglePublished = async (f) => {
    try {
      await axios.put(`${API}/admin/formations/${f.formation_id}`, { ...f, published: !f.published }, { withCredentials: true });
      toast.success(f.published ? 'Dépubliée' : 'Publiée');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const openDecision = (app, status) => {
    setDecisionModal({ app, status });
    const tmpl = status === 'accepted'
      ? `Bonjour ${app.full_name},\n\nVotre candidature à la formation a été acceptée. Nous vous contacterons sous peu pour les modalités pratiques.\n\nL'équipe CRESA`
      : `Bonjour ${app.full_name},\n\nNous avons examiné votre candidature avec attention. Malheureusement nous ne pouvons pas y donner suite favorablement pour cette session.\n\nL'équipe CRESA`;
    setDecisionMessage(tmpl);
  };

  const submitDecision = async () => {
    if (!decisionMessage.trim()) { toast.error('Message obligatoire'); return; }
    try {
      await axios.put(
        `${API}/admin/formation-applications/${decisionModal.app.application_id}/decision`,
        { status: decisionModal.status, message: decisionMessage },
        { withCredentials: true }
      );
      toast.success(decisionModal.status === 'accepted' ? 'Candidature acceptée' : 'Candidature refusée');
      setDecisionModal(null); setDecisionMessage('');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDeleteApp = async (id) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;
    try { await axios.delete(`${API}/admin/formation-applications/${id}`, { withCredentials: true }); toast.success('Supprimée'); fetchAll(); }
    catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="formations-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Formations à publier ({formations.length})
        </h2>
        <div className="flex gap-2">
          <button onClick={() => setTab('list')} data-testid="tab-formations-list" className={`px-4 py-2 rounded-md text-sm font-bold border-2 ${tab === 'list' ? 'bg-[#4A6B48] border-[#4A6B48] text-white' : 'border-[#E5E3DB] text-[#5C665A]'}`}>
            <GraduationCap size={14} className="inline mr-1" /> Formations
          </button>
          <button onClick={() => setTab('applications')} data-testid="tab-formations-applications" className={`px-4 py-2 rounded-md text-sm font-bold border-2 ${tab === 'applications' ? 'bg-[#4A6B48] border-[#4A6B48] text-white' : 'border-[#E5E3DB] text-[#5C665A]'}`}>
            <Inbox size={14} className="inline mr-1" /> Candidatures ({applications.length})
          </button>
        </div>
      </div>

      {tab === 'list' && (
        <>
          <div className="mb-4">
            <button onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData(EMPTY); }} className="btn-primary inline-flex items-center" data-testid="add-formation-button">
              <Plus size={20} className="mr-2" />
              {showForm ? 'Annuler' : 'Publier une nouvelle formation'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="card mb-6" data-testid="formation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Titre *</label>
                  <input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="formation-title-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Thème *</label>
                  <input required value={formData.theme} onChange={(e) => setFormData({ ...formData, theme: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="formation-theme-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Date *</label>
                  <input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="formation-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Lieu</label>
                  <input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" placeholder="Ex: Bukavu" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Participants attendus *</label>
                  <input type="number" required min="1" value={formData.expected_participants} onChange={(e) => setFormData({ ...formData, expected_participants: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Modalité *</label>
                  <select value={formData.modality} onChange={(e) => setFormData({ ...formData, modality: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg">
                    {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Catégorie de candidat *</label>
                  <input required value={formData.candidate_category} onChange={(e) => setFormData({ ...formData, candidate_category: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" placeholder="Ex: Universitaire ou sans niveau" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Description (optionnel)</label>
                  <textarea rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Conditions *</label>
                  <textarea required rows={3} value={formData.conditions} onChange={(e) => setFormData({ ...formData, conditions: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" placeholder="Ex: Maîtrise du français, engagement…" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Résultats attendus *</label>
                  <textarea required rows={3} value={formData.expected_results} onChange={(e) => setFormData({ ...formData, expected_results: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" placeholder="À la fin, les participants…" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Lien Google Form (optionnel)</label>
                  <input type="url" value={formData.google_form_url} onChange={(e) => setFormData({ ...formData, google_form_url: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" placeholder="https://docs.google.com/forms/..." />
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="f-pub" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} className="h-4 w-4" />
                  <label htmlFor="f-pub" className="text-sm text-[#2E332D]">Publier immédiatement</label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="btn-primary" data-testid="submit-formation-button">{editingId ? 'Mettre à jour' : 'Publier'}</button>
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setFormData(EMPTY); }} className="btn-secondary">Annuler</button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
          ) : formations.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucune formation. Cliquez sur &ldquo;Publier une nouvelle formation&rdquo;.</p>
          ) : (
            <div className="space-y-3">
              {formations.map((f) => {
                const apps = applications.filter((a) => a.formation_id === f.formation_id);
                return (
                  <div key={f.formation_id} className="card" data-testid={`admin-formation-${f.formation_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${f.published ? 'bg-[#4A6B48]/10 text-[#4A6B48]' : 'bg-gray-100 text-gray-600'}`}>
                            {f.published ? 'Publiée' : 'Brouillon'}
                          </span>
                          <span className="text-xs inline-flex items-center gap-1 text-[#5C665A]"><Calendar size={12} /> {new Date(f.date).toLocaleDateString('fr-FR')}</span>
                          {f.location && <span className="text-xs inline-flex items-center gap-1 text-[#5C665A]"><MapPin size={12} /> {f.location}</span>}
                          <span className="text-xs inline-flex items-center gap-1 text-[#5C665A]"><Users size={12} /> {f.expected_participants} attendus</span>
                          <span className="text-xs font-bold text-[#D87D4A]">{apps.length} candidature{apps.length > 1 ? 's' : ''}</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#2E332D]">{f.title}</h3>
                        <p className="text-sm text-[#5C665A]"><strong>Thème :</strong> {f.theme}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => togglePublished(f)} className="p-2 hover:bg-gray-100 rounded">
                          {f.published ? <EyeOff size={16} className="text-orange-600" /> : <Eye size={16} className="text-[#4A6B48]" />}
                        </button>
                        <button onClick={() => handleEdit(f)} className="p-2 hover:bg-gray-100 rounded"><Edit2 size={16} className="text-[#4A6B48]" /></button>
                        <button onClick={() => handleDelete(f.formation_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={16} className="text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'applications' && (
        <>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
          ) : applications.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucune candidature pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {applications.map((a) => {
                const formation = formations.find((f) => f.formation_id === a.formation_id);
                return (
                  <div key={a.application_id} className="card" data-testid={`admin-application-${a.application_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                            a.status === 'accepted' ? 'bg-[#4A6B48]/10 text-[#4A6B48]' :
                            a.status === 'rejected' ? 'bg-red-100 text-red-600' :
                            'bg-orange-100 text-orange-600'
                          }`}>
                            {a.status === 'accepted' ? 'Acceptée' : a.status === 'rejected' ? 'Refusée' : 'En attente'}
                          </span>
                          <span className="text-xs text-[#5C665A]">{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                          {formation && <span className="text-xs font-bold text-[#4A6B48]">→ {formation.title}</span>}
                        </div>
                        <h3 className="text-lg font-bold text-[#2E332D]">{a.full_name}</h3>
                        <p className="text-xs text-[#5C665A]">{a.email}{a.phone && ` · ${a.phone}`}{a.organization && ` · ${a.organization}`}</p>
                        <p className="text-xs text-[#5C665A]">Profil : {a.profile_type}{a.education_level && ` · Niveau : ${a.education_level}`}</p>
                        <p className="text-sm text-[#2E332D] mt-2 italic">&ldquo;{a.motivation}&rdquo;</p>
                        {a.admin_message && (
                          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                            <p className="text-xs font-bold text-purple-900 mb-1">Message admin envoyé :</p>
                            <p className="text-sm text-[#2E332D] whitespace-pre-wrap">{a.admin_message}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {a.status === 'pending' && (
                          <>
                            <button onClick={() => openDecision(a, 'accepted')} className="p-2 bg-[#4A6B48]/10 hover:bg-[#4A6B48]/20 rounded" title="Accepter" data-testid={`accept-app-${a.application_id}`}>
                              <Check size={16} className="text-[#4A6B48]" />
                            </button>
                            <button onClick={() => openDecision(a, 'rejected')} className="p-2 bg-red-100 hover:bg-red-200 rounded" title="Refuser" data-testid={`reject-app-${a.application_id}`}>
                              <X size={16} className="text-red-600" />
                            </button>
                          </>
                        )}
                        <button onClick={() => handleDeleteApp(a.application_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={16} className="text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Decision Modal */}
      {decisionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-[#2E332D] mb-3">
              {decisionModal.status === 'accepted' ? 'Accepter' : 'Refuser'} la candidature de {decisionModal.app.full_name}
            </h3>
            <p className="text-sm text-[#5C665A] mb-3">Ce message sera envoyé par email au candidat.</p>
            <textarea rows={8} value={decisionMessage} onChange={(e) => setDecisionMessage(e.target.value)} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg text-sm" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setDecisionModal(null); setDecisionMessage(''); }} className="btn-secondary">Annuler</button>
              <button onClick={submitDecision} className={`px-4 py-2 rounded-md font-bold text-white ${decisionModal.status === 'accepted' ? 'bg-[#4A6B48] hover:bg-[#3B5539]' : 'bg-red-500 hover:bg-red-600'}`} data-testid="confirm-application-decision">
                {decisionModal.status === 'accepted' ? "Confirmer l'acceptation" : "Confirmer le refus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
